import React from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Input,
  Button,
  Table,
  Alert,
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class AdminSongBookDetail extends React.Component {
  state = {
    isLoading: true,
    isSaving: false,
    unauthorized: false,
    notFound: false,
    songBook: null,         // { id, title, shortTitle }
    rows: [],               // current (possibly edited) rows
    originalRows: [],       // clean baseline after load/save
    saveError: null,
    saveSuccess: false,
  };

  componentDidMount() {
    try {
      const customer = sessionStorage.getItem("customer");
      const parsed = customer ? JSON.parse(customer) : null;
      if (!parsed?.user?.isAdmin) {
        this.setState({ unauthorized: true });
        return;
      }
      const { songBookId } = this.props.params;
      this.fetchData(songBookId);
    } catch (e) {
      console.error("AdminSongBookDetail componentDidMount error:", e);
      this.setState({ unauthorized: true });
    }
  }

  fetchData = (songBookId) => {
    this.setState({ isLoading: true, saveError: null, saveSuccess: false });

    // Load the song book summary (from the list endpoint) and its songs in parallel
    Promise.all([
      Api.get(`/api/admin/song-books?search=`),
      Api.get(`/api/admin/song-books/${songBookId}/songs`),
    ])
      .then(([booksResp, songsResp]) => {
        const songBook = booksResp.data.find(sb => sb.id === songBookId) || null;
        if (!songBook) {
          this.setState({ notFound: true, isLoading: false });
          return;
        }
        const rows = songsResp.data;
        this.setState({
          songBook,
          rows: rows.map(r => ({ ...r })),
          originalRows: rows.map(r => ({ ...r })),
          isLoading: false,
        });
      })
      .catch(err => {
        console.error("Error loading song book detail:", err);
        if (err.response?.status === 404) {
          this.setState({ notFound: true, isLoading: false });
        } else {
          this.setState({ isLoading: false });
        }
      });
  };

  handleSongNumberChange = (wsbId, value) => {
    this.setState(prev => ({
      rows: prev.rows.map(r =>
        r.wsbId === wsbId ? { ...r, songBookSongNumber: value } : r
      ),
      saveSuccess: false,
    }));
  };

  isDirty = () => {
    const { rows, originalRows } = this.state;
    return rows.some((r, i) => r.songBookSongNumber !== originalRows[i]?.songBookSongNumber);
  };

  changedRows = () => {
    const { rows, originalRows } = this.state;
    return rows.filter((r, i) => r.songBookSongNumber !== originalRows[i]?.songBookSongNumber);
  };

  handleSave = () => {
    const { songBook } = this.state;
    const changed = this.changedRows();
    if (!changed.length) return;

    this.setState({ isSaving: true, saveError: null, saveSuccess: false });

    const payload = {
      updates: changed.map(r => ({
        wsbId: r.wsbId,
        songBookSongNumber: r.songBookSongNumber?.trim() ?? "",
      })),
    };

    Api.put(`/api/admin/song-books/${songBook.id}/song-numbers`, payload)
      .then(() => {
        this.setState(prev => ({
          isSaving: false,
          saveSuccess: true,
          // Commit current rows as the new baseline
          originalRows: prev.rows.map(r => ({ ...r })),
        }));
      })
      .catch(err => {
        console.error("Bulk save failed:", err);
        const msg = err.response?.data || "Save failed. See console for details.";
        this.setState({ isSaving: false, saveError: String(msg) });
      });
  };

  handleCancel = () => {
    this.setState(prev => ({
      rows: prev.originalRows.map(r => ({ ...r })),
      saveError: null,
      saveSuccess: false,
    }));
  };

  render() {
    if (!sessionStorage.getItem("customer")) {
      return sessionEnded();
    }
    if (this.state.unauthorized) return <Navigate to="/" />;

    const {
      isLoading, isSaving, notFound,
      songBook, rows,
      saveError, saveSuccess,
    } = this.state;

    if (notFound) {
      return (
        <Container className="mt-5">
          <p className="text-muted">Song book not found.</p>
          <Button color="link" className="p-0" onClick={() => this.props.navigate("/admin/song-books")}>
            &larr; Back to list
          </Button>
        </Container>
      );
    }

    const dirty = this.isDirty();
    const changedCount = this.changedRows().length;

    return (
      <>
        <SeoNoIndex noindex={true} />

        <div className="position-relative">
          <section className="section section-lg section-shaped pb-250">
            <div className="shape shape-style-1 shape-default">
              <span/><span/><span/><span/><span/><span/><span/><span/><span/>
            </div>
            <Container className="py-lg-md d-flex">
              <div className="col px-0">
                <Row>
                  <Col lg="10">
                    <h1 className="display-3 text-white">
                      {songBook
                        ? <>Edit Song Book: <span>{songBook.title}</span></>
                        : "Loading…"}
                    </h1>
                    {songBook?.shortTitle && (
                      <p className="lead text-white">{songBook.shortTitle}</p>
                    )}
                  </Col>
                </Row>
              </div>
            </Container>
            <div className="separator separator-bottom separator-skew">
              <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                <polygon className="fill-white" points="2560 0 2560 100 0 100"/>
              </svg>
            </div>
          </section>
        </div>

        <section className="section section-lg pt-lg-0 mt--200">
          <Container>
            <Row className="justify-content-center">
              <Col lg="12">
                <Card className="shadow border-0">
                  <CardBody className="p-4">

                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => this.props.navigate("/admin/song-books")}
                      >
                        &larr; Back to list
                      </Button>
                      <div className="d-flex gap-2 align-items-center">
                        {dirty && (
                          <span className="text-muted small">
                            {changedCount} unsaved {changedCount === 1 ? "change" : "changes"}
                          </span>
                        )}
                        <Button
                          color="secondary"
                          onClick={this.handleCancel}
                          disabled={!dirty || isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          onClick={this.handleSave}
                          disabled={!dirty || isSaving}
                        >
                          {isSaving ? "Saving…" : "Save"}
                        </Button>
                      </div>
                    </div>

                    {saveSuccess && (
                      <Alert color="success" className="mb-3">
                        Song numbers saved successfully.
                      </Alert>
                    )}
                    {saveError && (
                      <Alert color="danger" className="mb-3">
                        {saveError}
                      </Alert>
                    )}

                    {isLoading ? (
                      <p className="text-muted">Loading songs…</p>
                    ) : rows.length === 0 ? (
                      <p className="text-muted">No songs found in this song book.</p>
                    ) : (
                      <Table hover responsive size="sm">
                        <thead>
                          <tr>
                            <th style={{ width: "120px" }}>Current no.</th>
                            <th>Title</th>
                            <th>Alternative title</th>
                            <th>Authors</th>
                            <th style={{ width: "80px" }}>TWB ID</th>
                            <th style={{ width: "140px" }}>Update to</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, idx) => {
                            const original = this.state.originalRows[idx];
                            const changed = original && row.songBookSongNumber !== original.songBookSongNumber;
                            return (
                              <tr key={row.wsbId}>
                                <td className="text-muted font-monospace">
                                  {original?.songBookSongNumber || "—"}
                                </td>
                                <td>
                                  <strong>{row.title}</strong>
                                </td>
                                <td className="text-muted">
                                  {row.alternativeTitle || "—"}
                                </td>
                                <td className="text-muted small">
                                  {row.authorNames?.length > 0
                                    ? row.authorNames.join(", ")
                                    : "—"}
                                </td>
                                <td className="text-muted small font-monospace">
                                  {row.wordsId}
                                </td>
                                <td>
                                  <Input
                                    type="text"
                                    bsSize="sm"
                                    value={row.songBookSongNumber ?? ""}
                                    onChange={e =>
                                      this.handleSongNumberChange(row.wsbId, e.target.value)
                                    }
                                    placeholder="e.g. 42"
                                    style={changed ? { borderColor: "#2dce89", fontWeight: "bold" } : {}}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}

                    {!isLoading && rows.length > 0 && (
                      <div className="d-flex justify-content-end gap-2 mt-3">
                        {dirty && (
                          <span className="text-muted small align-self-center">
                            {changedCount} unsaved {changedCount === 1 ? "change" : "changes"}
                          </span>
                        )}
                        <Button
                          color="secondary"
                          onClick={this.handleCancel}
                          disabled={!dirty || isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          onClick={this.handleSave}
                          disabled={!dirty || isSaving}
                        >
                          {isSaving ? "Saving…" : "Save"}
                        </Button>
                      </div>
                    )}

                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>
      </>
    );
  }
}

function withRouterParamsAndNavigate(Component) {
  return function Wrapped(props) {
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} navigate={navigate} params={params} />;
  };
}

export default withRouterParamsAndNavigate(AdminSongBookDetail);
